import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { Card, C } from './UI';
import { ContractorListItem, JobListItem, ShreddingBinLocationDto } from '../services/api';
import { useBranding } from '../context/BrandingContext';

type MapView = 'contractors' | 'jobs' | 'all';

interface LivePosition {
    jobId: string;
    lat: number;
    lng: number;
    jobStatus: string;
    timestamp: string;
}

declare global {
    interface Window { google: any; }
}

export function ContractorMap({
    contractors,
    jobs,
    bins,
}: {
    contractors: ContractorListItem[];
    jobs: JobListItem[];
    // Shredding bins out in the field (see IShreddingService.GetBinLocationsAsync) — a parallel
    // layer, not a mapView mode, so it's independently toggleable alongside contractors/jobs.
    // Omitted entirely (not just empty) when the tenant doesn't have Document Shredding enabled.
    bins?: ShreddingBinLocationDto[];
}) {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const contractorLabelPlural = branding.contractorNoun ? branding.contractorNoun + 's' : 'Contractors';
    const mapRef = useRef<HTMLDivElement>(null);
    const googleMapRef = useRef<any>(null);
    const staticMarkersRef = useRef<Map<string, any>>(new Map());
    const liveMarkersRef = useRef<Map<string, { marker: any; pulse: NodeJS.Timeout; routeLine?: any }>>(new Map());
    const infoWindowRef = useRef<any>(null);
    const hubRef = useRef<signalR.HubConnection | null>(null);
    const hasUserMovedMap = useRef(false);

    const [mapView, setMapView] = useState<MapView>('all');
    const [showBins, setShowBins] = useState(true);
    const [ready, setReady] = useState(false);
    const [liveCount, setLiveCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const searchMarkerRef = useRef<any>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const autocompleteRef = useRef<any>(null);

    // Wait for Google Maps
    useEffect(() => {
        const check = () => {
            if (window.google?.maps) setReady(true);
            else setTimeout(check, 200);
        };
        check();
    }, []);

    // Init map
    useEffect(() => {
        if (!ready || !mapRef.current || googleMapRef.current) return;

        const initMap = (center: { lat: number; lng: number }) => {
            googleMapRef.current = new window.google.maps.Map(mapRef.current!, {
                center,
                zoom: 11,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
                styles: [
                    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
                ]
            });

            googleMapRef.current.addListener('dragstart', () => {
                hasUserMovedMap.current = true;
            });

            infoWindowRef.current = new window.google.maps.InfoWindow();
        };

        const fallback = { lat: 34.7304, lng: -86.5861 };

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                pos => initMap({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                ()  => initMap(fallback),
                { timeout: 5000 }
            );
        } else {
            initMap(fallback);
        }
    }, [ready]);

    // Draw route line from contractor to pickup
const drawRouteLine = useCallback((contractorLat: number, contractorLng: number, jobId: string) => {
    if (!googleMapRef.current) return null;

    const job = jobs.find(j => j.id === jobId);
    if (!job?.pickupLat || !job?.pickupLng) return null;

    const directionsService = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
        map: googleMapRef.current,
        suppressMarkers: true,  // don't show A/B markers — we have our own
        polylineOptions: {
            strokeColor: '#1D9E75',
            strokeWeight: 4,
            strokeOpacity: 0.8
        }
    });

    directionsService.route({
        origin: { lat: contractorLat, lng: contractorLng },
        destination: { lat: job.pickupLat, lng: job.pickupLng },
        travelMode: window.google.maps.TravelMode.DRIVING
    }, (result: any, status: any) => {
        if (status === 'OK') {
            directionsRenderer.setDirections(result);
        }
    });

    return directionsRenderer; // return renderer instead of polyline so we can clear it
}, [jobs]);

    // Connect to SignalR for live updates
    useEffect(() => {
        const token = localStorage.getItem('br_token');
        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        hub.on('ContractorLocationUpdated', (push: LivePosition) => {
            if (!push.lat || !push.lng || push.lat === 0) return;

            const markerKey = `live-${push.jobId}`;

            if (liveMarkersRef.current.has(markerKey)) {
                // Update existing marker and route line
                const val = liveMarkersRef.current.get(markerKey)!;
                val.marker.setPosition({ lat: push.lat, lng: push.lng });

                // Update route line
                if (val.routeLine) {
                    val.routeLine.setMap(null);
                }
                const newLine = drawRouteLine(push.lat, push.lng, push.jobId);
                liveMarkersRef.current.set(markerKey, { ...val, routeLine: newLine || undefined });

            } else if (googleMapRef.current) {
                // Find contractor name from active jobs
                const job = jobs.find(j => j.id === push.jobId);
                const contractorName = job?.contractorName || 'Contractor';
                const initials = contractorName.split(' ').map((n: string) => n[0]).join('').slice(0, 2);

                // Create new live marker
                const marker = new window.google.maps.Marker({
                    position: { lat: push.lat, lng: push.lng },
                    map: googleMapRef.current,
                    title: contractorName,
                    icon: {
                        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 8,
                        fillColor: '#1D9E75',
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 2,
                        rotation: 0,
                    },
                    label: {
                        text: initials,
                        color: '#fff',
                        fontSize: '9px',
                        fontWeight: 'bold'
                    },
                    zIndex: 999
                });

                // Pulse animation
                let visible = true;
                const pulse = setInterval(() => {
                    visible = !visible;
                    marker.setOpacity(visible ? 1 : 0.5);
                }, 800);

                marker.addListener('click', () => {
                    infoWindowRef.current.setContent(`
                        <div style="font-family:Arial;padding:8px;min-width:180px">
                            <div style="font-weight:700;font-size:14px;color:#0F6E56;margin-bottom:6px">
                                🟢 ${contractorName}
                            </div>
                            <div style="font-size:12px;color:#666;margin-bottom:4px">
                                📦 ${job?.pickupAddress?.split(',')[0] || 'Active job'}
                            </div>
                            <div style="font-size:12px;margin-bottom:4px">
                                Status: <strong>${push.jobStatus}</strong>
                            </div>
                            <div style="font-size:12px;color:#666;margin-bottom:4px">
                                Service: ${job?.serviceType || '—'}
                            </div>
                            <div style="font-size:11px;color:#aaa">
                                Last ping: ${new Date(push.timestamp).toLocaleTimeString()}
                            </div>
                        </div>
                    `);
                    infoWindowRef.current.open(googleMapRef.current, marker);
                });

                // Draw route line to pickup
                const routeLine = drawRouteLine(push.lat, push.lng, push.jobId) || undefined;

                liveMarkersRef.current.set(markerKey, { marker, pulse, routeLine });
                setLiveCount(liveMarkersRef.current.size);
            }
        });

        hub.start()
            .then(() => hub.invoke('JoinAdminPool').catch(() => {}))
            .catch(console.error);

        hub.onreconnected(() => {
            hub.invoke('JoinAdminPool').catch(() => {});
        });

        hubRef.current = hub;

        return () => {
            liveMarkersRef.current.forEach(val => {
                clearInterval(val.pulse);
                val.marker.setMap(null);
                if (val.routeLine) val.routeLine.setMap(null);
            });
            liveMarkersRef.current.clear();
            hub.stop();
        };
    }, [jobs, drawRouteLine]);

    // Build static markers from DB data
    const updateMarkers = useCallback(() => {
        if (!googleMapRef.current) return;

        // Clear static markers
        staticMarkersRef.current.forEach(m => m.setMap(null));
        staticMarkersRef.current.clear();

        const bounds = new window.google.maps.LatLngBounds();
        let markerCount = 0;

        const addMarker = (
            lat: number, lng: number,
            color: string,
            shape: any,
            scale: number,
            label: string | undefined,
            infoContent: string,
            key: string
        ) => {
            const marker = new window.google.maps.Marker({
                position: { lat, lng },
                map: googleMapRef.current,
                icon: {
                    path: shape,
                    scale,
                    fillColor: color,
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                },
                label: label ? { text: label, color: '#fff', fontSize: '10px', fontWeight: 'bold' } : undefined
            });

            marker.addListener('click', () => {
                infoWindowRef.current.setContent(infoContent);
                infoWindowRef.current.open(googleMapRef.current, marker);
            });

            staticMarkersRef.current.set(key, marker);
            bounds.extend({ lat, lng });
            markerCount++;
        };

        // Contractors
        if (mapView === 'contractors' || mapView === 'all') {
            contractors
                .filter(c => c.currentLat && c.currentLng)
                .forEach(c => {
                    const color = c.isOnline ? '#1D9E75' : '#999';
                    const initials = c.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2);
                    addMarker(
                        c.currentLat!, c.currentLng!,
                        color,
                        window.google.maps.SymbolPath.CIRCLE,
                        14, initials,
                        `<div style="font-family:Arial;padding:8px;min-width:160px">
                            <div style="font-weight:700;font-size:14px;margin-bottom:4px">${c.name}</div>
                            <div style="font-size:12px;color:#666;margin-bottom:4px">${c.vehicleDescription || 'No vehicle info'}</div>
                            <div style="font-size:12px;margin-bottom:6px">
                                <span style="background:${c.isOnline ? '#E1F5EE' : '#F1EFE8'};color:${c.isOnline ? '#0F6E56' : '#666'};padding:2px 8px;border-radius:10px;font-weight:600">
                                    ${c.isOnline ? 'Online' : 'Offline'}
                                </span>
                            </div>
                            <div style="font-size:12px;color:#888">★ ${c.averageRating.toFixed(1)} · ${c.totalJobsCompleted} jobs</div>
                        </div>`,
                        `contractor-${c.id}`
                    );
                });
        }

        // Jobs
        if (mapView === 'jobs' || mapView === 'all') {
            jobs
                .filter(j => j.pickupLat && j.pickupLng && !['Completed', 'Cancelled'].includes(j.status))
                .forEach(j => {
                    const isPending = j.status === 'Pending';
                    const color = isPending ? '#EF9F27' : '#534AB7';
                    addMarker(
                        j.pickupLat, j.pickupLng,
                        color,
                        window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                        10, undefined,
                        `<div style="font-family:Arial;padding:8px;min-width:180px">
                            <div style="font-weight:700;font-size:13px;margin-bottom:4px">${j.pickupAddress.split(',')[0]}</div>
                            <div style="font-size:12px;color:#666;margin-bottom:4px">${j.serviceType} · $${j.totalPrice.toFixed(2)}</div>
                            <div style="font-size:12px;margin-bottom:4px">
                                <span style="background:#E1F5EE;color:#0F6E56;padding:2px 8px;border-radius:10px;font-weight:600">${j.status}</span>
                            </div>
                            <div style="font-size:12px;color:#888">Customer: ${j.customerName}</div>
                            ${j.contractorName ? `<div style="font-size:12px;color:#888">Contractor: ${j.contractorName}</div>` : ''}
                        </div>`,
                        `job-${j.id}`
                    );
                });
        }

        // Shredding bins — independent layer, shown regardless of the contractors/jobs mapView
        // toggle. Each bin's position is its most recent GPS-tagged scan location (see
        // IShreddingService.GetBinLocationsAsync — bins have no live lat/lng of their own).
        if (showBins && bins) {
            bins.forEach(b => {
                const color = b.status === 'InTransitToDestruction' ? '#EF9F27' : '#6366F1';
                addMarker(
                    b.lat, b.lng,
                    color,
                    window.google.maps.SymbolPath.CIRCLE,
                    9, undefined,
                    `<div style="font-family:Arial;padding:8px;min-width:160px">
                        <div style="font-weight:700;font-size:13px;margin-bottom:4px">📦 ${b.label}</div>
                        ${b.sizeName ? `<div style="font-size:12px;color:#666;margin-bottom:4px">${b.sizeName}</div>` : ''}
                        <div style="font-size:12px">
                            <span style="background:#EEF2FF;color:#4338CA;padding:2px 8px;border-radius:10px;font-weight:600">${b.status}</span>
                        </div>
                    </div>`,
                    `bin-${b.id}`
                );
            });
        }

        if (markerCount > 0 && !hasUserMovedMap.current) {
            googleMapRef.current.fitBounds(bounds);
            if (markerCount === 1) googleMapRef.current.setZoom(14);
        }
    }, [contractors, jobs, mapView, bins, showBins]);

    useEffect(() => {
        updateMarkers();
    }, [updateMarkers]);

    // Init Google Places Autocomplete on search input
    useEffect(() => {
        if (!ready || !searchInputRef.current || autocompleteRef.current) return;

        autocompleteRef.current = new window.google.maps.places.Autocomplete(
            searchInputRef.current,
            { componentRestrictions: { country: 'us' } }
        );

        autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current.getPlace();
            if (!place.geometry?.location) return;

            hasUserMovedMap.current = true;
            googleMapRef.current.panTo(place.geometry.location);
            googleMapRef.current.setZoom(15);

            if (searchMarkerRef.current) searchMarkerRef.current.setMap(null);

            searchMarkerRef.current = new window.google.maps.Marker({
                position: place.geometry.location,
                map: googleMapRef.current,
                title: place.formatted_address,
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 10,
                    fillColor: '#C0392B',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                },
                animation: window.google.maps.Animation.DROP,
                zIndex: 1000,
            });

            infoWindowRef.current.setContent(`
                <div style="font-family:Arial;padding:8px;min-width:180px">
                    <div style="font-weight:700;font-size:13px;color:#C0392B;margin-bottom:4px">📍 Searched location</div>
                    <div style="font-size:12px;color:#444">${place.formatted_address}</div>
                </div>
            `);
            infoWindowRef.current.open(googleMapRef.current, searchMarkerRef.current);

            setSearchQuery(place.formatted_address || '');
        });
    }, [ready]);

    const searchAddress = () => {
        if (!searchQuery.trim() || !window.google?.maps || !googleMapRef.current) return;
        setSearching(true);

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: searchQuery }, (results: any, status: any) => {
            setSearching(false);
            if (status !== 'OK' || !results?.length) {
                alert('Address not found. Try a more specific address.');
                return;
            }

            const location = results[0].geometry.location;
            hasUserMovedMap.current = true;

            // Pan and zoom to address
            googleMapRef.current.panTo(location);
            googleMapRef.current.setZoom(15);

            // Remove previous search marker
            if (searchMarkerRef.current) {
                searchMarkerRef.current.setMap(null);
            }

            // Drop a pin at the searched address
            searchMarkerRef.current = new window.google.maps.Marker({
                position: location,
                map: googleMapRef.current,
                title: results[0].formatted_address,
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 10,
                    fillColor: '#C0392B',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                },
                animation: window.google.maps.Animation.DROP,
                zIndex: 1000,
            });

            // Show info window with address
            infoWindowRef.current.setContent(`
                <div style="font-family:Arial;padding:8px;min-width:180px">
                    <div style="font-weight:700;font-size:13px;color:#C0392B;margin-bottom:4px">📍 Searched location</div>
                    <div style="font-size:12px;color:#444">${results[0].formatted_address}</div>
                </div>
            `);
            infoWindowRef.current.open(googleMapRef.current, searchMarkerRef.current);
        });
    };

    const clearSearch = () => {
        setSearchQuery('');
        if (searchMarkerRef.current) {
            searchMarkerRef.current.setMap(null);
            searchMarkerRef.current = null;
        }
        infoWindowRef.current?.close();
        hasUserMovedMap.current = false;
        updateMarkers();
    };

    const onlineCount = contractors.filter(c => c.isOnline).length;
    const onlineNoLocation = contractors.filter(c => c.isOnline && !c.currentLat).length;
    const activeJobCount = jobs.filter(j => !['Completed', 'Cancelled'].includes(j.status)).length;
    const pendingCount = jobs.filter(j => j.status === 'Pending').length;

    return (
        <div>
            {/* Stats row */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <div style={{ background: '#E1F5EE', borderRadius: 8, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#1D9E75' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0F6E56' }}>
                        {onlineCount} online contractor{onlineCount !== 1 ? 's' : ''}
                        {onlineNoLocation > 0 && <span style={{ fontWeight: 400, marginLeft: 4 }}>({onlineNoLocation} no GPS yet)</span>}
                    </span>
                </div>
                <div style={{ background: '#EEEDFE', borderRadius: 8, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#534AB7' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#26215C' }}>{activeJobCount} active jobs</span>
                </div>
                <div style={{ background: '#FAEEDA', borderRadius: 8, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF9F27' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#854F0B' }}>{pendingCount} pending pickup</span>
                </div>
                {liveCount > 0 && (
                    <div style={{ background: '#E1F5EE', borderRadius: 8, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #1D9E75' }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#1D9E75', animation: 'mpulse 1s infinite' }} />
                        <style>{`@keyframes mpulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#0F6E56' }}>{liveCount} live tracking</span>
                    </div>
                )}
            </div>

            {/* Radio toggle */}
            <Card style={{ marginBottom: 16, padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.textSub }}>Show on map:</span>
                    {([
                        { value: 'all', label: '🗺️ Everything' },
                        { value: 'contractors', label: `🚚 ${contractorLabelPlural} only` },
                        { value: 'jobs', label: '📦 Jobs only' },
                    ] as { value: MapView; label: string }[]).map(opt => (
                        <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="mapView"
                                value={opt.value}
                                checked={mapView === opt.value}
                                onChange={() => { setMapView(opt.value); hasUserMovedMap.current = false; }}
                                style={{ accentColor: C.greenMid, width: 15, height: 15 }}
                            />
                            <span style={{ fontSize: 13, color: C.text, fontWeight: mapView === opt.value ? 600 : 400 }}>
                                {opt.label}
                            </span>
                        </label>
                    ))}
                    {bins && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={showBins}
                                onChange={e => setShowBins(e.target.checked)}
                                style={{ accentColor: '#6366F1', width: 15, height: 15 }}
                            />
                            <span style={{ fontSize: 13, color: C.text, fontWeight: showBins ? 600 : 400 }}>
                                📦 Shredding bins
                            </span>
                        </label>
                    )}
                    <button
                        onClick={() => { hasUserMovedMap.current = false; updateMarkers(); }}
                        style={{ marginLeft: 'auto', fontSize: 12, color: C.greenMid, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                    >
                        ↺ Reset view
                    </button>
                </div>
            </Card>

            {/* Address search bar */}
            <Card style={{ marginBottom: 16, padding: '12px 16px' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 18 }}>🔍</span>
                    <input
                        type="text"
                        ref={searchInputRef}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && searchAddress()}
                        placeholder="Search any address to jump to location..."
                        style={{
                            flex: 1, padding: '8px 12px', border: `1px solid ${C.border}`,
                            borderRadius: 8, fontSize: 13, outline: 'none',
                            fontFamily: 'inherit',
                        }}
                    />
                    <button
                        onClick={searchAddress}
                        disabled={!searchQuery.trim() || searching}
                        style={{
                            padding: '8px 16px', background: C.greenMid, color: '#fff',
                            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                            cursor: 'pointer', opacity: (!searchQuery.trim() || searching) ? 0.5 : 1,
                        }}
                    >
                        {searching ? '...' : 'Go'}
                    </button>
                    {searchMarkerRef.current && (
                        <button
                            onClick={clearSearch}
                            style={{
                                padding: '8px 12px', background: C.bg, color: C.textSub,
                                border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13,
                                cursor: 'pointer',
                            }}
                        >
                            ✕ Clear
                        </button>
                    )}
                </div>
            </Card>

            {/* Map */}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
                {!ready && (
                    <div style={{ height: 520, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSub }}>
                        Loading map...
                    </div>
                )}
                <div ref={mapRef} style={{ height: 520, width: '100%', display: ready ? 'block' : 'none' }} />
            </Card>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#1D9E75' }} />
                    <span style={{ fontSize: 12, color: C.textSub }}>Online {contractorLabel.toLowerCase()} (last known)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#999' }} />
                    <span style={{ fontSize: 12, color: C.textSub }}>Offline {contractorLabel.toLowerCase()}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#534AB7' }} />
                    <span style={{ fontSize: 12, color: C.textSub }}>Active job pickup location</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#EF9F27' }} />
                    <span style={{ fontSize: 12, color: C.textSub }}>Pending pickup</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 14, height: 14, background: '#1D9E75', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
                    <span style={{ fontSize: 12, color: C.textSub }}>Live {contractorLabel.toLowerCase()} + route line</span>
                </div>
            </div>
        </div>
    );
}
